/* @jsxImportSource @emotion/react */
import React from 'react'

import { useActiveHeadingId } from '../../hooks/useActiveHeadingId'
import { ArticleAuthorBios } from './ArticleAuthorBios'
import { ArticleHeader } from './ArticleHeader'
import { ArticleHero } from './ArticleHero'
import { ArticleSidebar } from './ArticleSidebar'
import {
  ARTICLE_COLUMN_GAP,
  ARTICLE_SIDEBAR_BREAKPOINT,
  ARTICLE_SIDEBAR_WIDTH,
  ARTICLE_STICKY_GAP,
  DEFAULT_SHARE_LABEL,
  DEFAULT_SHARE_ON_LINKEDIN_LABEL,
  DEFAULT_SHARE_ON_X_LABEL,
  DEFAULT_STICKY_TOP_OFFSET,
  DEFAULT_TOC_LABEL,
} from './constants'
import type { ArticleLayoutProps } from './types'
import { useArticleHeadings } from './useArticleHeadings'

import './article-body.css'

const gridCss = {
  display: 'grid',
  // minmax(0, …) so a wide <pre> or table can't blow the column out.
  gridTemplateColumns: `${ARTICLE_SIDEBAR_WIDTH}px minmax(0, 1fr)`,
  gap: ARTICLE_COLUMN_GAP,
  alignItems: 'start',
  marginTop: 56,
  [ARTICLE_SIDEBAR_BREAKPOINT]: {
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: 32,
  },
} as const

/**
 * Article page body — back link, title, authors, hero and a two-column layout
 * with a sticky table of contents. It fills whatever it is placed in: the
 * caller wraps it in a page section (WebsiteSection on the website), which
 * owns the max width, gutters and vertical rhythm.
 *
 * The table of contents is read from the rendered `children` after mount, so
 * any content works without cooperating. Pass `headings` when they are known
 * server-side to get a table of contents in the server HTML instead.
 */
export function ArticleLayout({
  stickyTopOffset = DEFAULT_STICKY_TOP_OFFSET,
  tocLabel = DEFAULT_TOC_LABEL,
  shareLabel = DEFAULT_SHARE_LABEL,
  shareOnXLabel = DEFAULT_SHARE_ON_X_LABEL,
  shareOnLinkedInLabel = DEFAULT_SHARE_ON_LINKEDIN_LABEL,
  ...props
}: ArticleLayoutProps) {
  const contentRef = React.useRef<HTMLDivElement>(null)
  const headings = useArticleHeadings(contentRef, props.headings)
  // The scan line sits just below where a heading lands, so the heading you
  // jump to is the one that lights up.
  const activeId = useActiveHeadingId(
    headings,
    stickyTopOffset + ARTICLE_STICKY_GAP + 8
  )
  const tocLabelId = React.useId()
  // Without anything to play, the hero image moves up beside the title instead
  // of taking a full-width band of its own.
  const isHeroBesideTitle = Boolean(
    props.heroImageUrl && !props.heroVideoEmbedUrl
  )
  const hero = (
    <ArticleHero
      imageUrl={props.heroImageUrl}
      imageAlt={props.heroImageAlt}
      imageFit={props.heroImageFit}
      backgroundColor={props.heroBackgroundColor}
      videoEmbedUrl={props.heroVideoEmbedUrl}
      watchButtonLabel={props.watchButtonLabel}
      autoPlayVideo={props.autoPlayVideo}
      title={props.title}
    />
  )

  return (
    <article
      className={props.className}
      style={
        {
          '--article-scroll-margin': `${stickyTopOffset + ARTICLE_STICKY_GAP}px`,
        } as React.CSSProperties
      }
    >
      <ArticleHeader
        title={props.title}
        description={props.description}
        date={props.date}
        dateTime={props.dateTime}
        readingTime={props.readingTime}
        category={props.category}
        authors={props.authors}
        authorsLabel={props.authorsLabel}
        backLabel={props.backLabel}
        backHref={props.backHref}
        ctaLabel={props.ctaLabel}
        ctaHref={props.ctaHref}
        aside={isHeroBesideTitle ? hero : undefined}
      />

      {!isHeroBesideTitle && <div className="margin-top-60">{hero}</div>}

      {props.belowHeader && (
        <div className="margin-top-60">{props.belowHeader}</div>
      )}

      <div css={gridCss}>
        <ArticleSidebar
          header={props.sidebarHeader}
          headings={headings}
          activeId={activeId}
          tocLabel={tocLabel}
          tocLabelId={tocLabelId}
          shareLabel={shareLabel}
          shareUrl={props.shareUrl}
          shareTitle={props.shareTitle ?? props.title}
          shareOnXLabel={shareOnXLabel}
          shareOnLinkedInLabel={shareOnLinkedInLabel}
          stickyTopOffset={stickyTopOffset}
          ctaLabel={props.showCtaInSidebar ? props.ctaLabel : undefined}
          ctaHref={props.showCtaInSidebar ? props.ctaHref : undefined}
        />
        <div className="min-width-zero">
          <div
            ref={contentRef}
            className="article-body simple-rich-text simple-rich-text-sanity large"
          >
            {props.children}
          </div>
          <ArticleAuthorBios
            authors={props.bioAuthors ?? props.authors}
            label={props.authorsLabel}
          />
        </div>
      </div>
    </article>
  )
}
